import React from 'react';
import QRCode from 'react-qr-code';
import { Mail, Phone, Globe, Building, MapPin, Linkedin, Twitter, Github, Instagram, Youtube, Facebook, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const VisitingCardDisplay = ({ card, hideQR = false }) => {
  const { user } = useAuth();
  const cardUrl = `${window.location.origin}/card/${card.username}`;
  const isInactive = card.isActive === false;
  
  // Track link click (exclude admin users and card owners)
  const trackLinkClick = async (linkType, linkValue) => {
    try {
      // Don't track if user is viewing their own card
      if (user && card.userId === user.uid) {
        console.log('⏭️ Skipping link click tracking for card owner');
        return;
      }
      
      // Check if user is admin
      const { adminAPI } = await import('../services/api');
      if (user) {
        try {
          const adminStatus = await adminAPI.checkAdminStatus();
          if (adminStatus.isAdmin) {
            console.log('⏭️ Skipping link click tracking for admin user');
            return;
          }
        } catch (error) {
          // If admin check fails, continue with tracking (fail-safe)
          console.log('Admin check failed, continuing with tracking');
        }
      }
      
      // Track the link click
      const { analyticsAPI } = await import('../services/api');
      const cardId = card._id || card.id;
      const cardUsername = card.username;
      
      if (cardId && cardUsername) {
        // Include viewer info for backend validation
        const viewerInfo = {
          viewerId: user?.uid || null,
          viewerEmail: user?.email || null,
          viewerName: user?.displayName || null,
          isAdminUser: false, // Explicitly mark as non-admin (already checked above)
          timestamp: new Date().toISOString()
        };
        
        await analyticsAPI.trackLinkClick(cardId, cardUsername, linkType, linkValue, viewerInfo);
        console.log(`✅ Tracked ${linkType} click`);
      }
    } catch (error) {
      console.error('Error tracking link click:', error);
    }
  };
  
  // Helper function to construct full URLs from IDs/usernames
  const constructUrl = (platform, value) => {
    if (!value) return null;
    
    // If value already starts with http, it's an old full URL - return as is for backward compatibility
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    
    // Construct URL from ID/username
    const urls = {
      linkedin: `https://linkedin.com/in/${value}`,
      twitter: `https://twitter.com/${value}`,
      github: `https://github.com/${value}`,
      instagram: `https://instagram.com/${value}`,
      facebook: `https://facebook.com/${value}`,
      youtube: value.startsWith('@') ? `https://youtube.com/${value}` : `https://youtube.com/channel/${value}`,
      whatsapp: `https://wa.me/${value.replace(/[^0-9]/g, '')}`
    };
    
    return urls[platform] || null;
  };
  
  // Construct website URL from name + TLD
  const websiteUrl = card.websiteName && card.websiteTLD
    ? `https://${card.websiteName}${card.websiteTLD}` 
    : card.website; // Fallback to old website field for backward compatibility
  
  // Simplified customization - no themes
  const qrColor = card.qrColor || '#0066CC';
  const textColor = card.textColor || '#1F2937';

  // Generate background style based on card.backgroundImage
  const getBackgroundStyle = () => {
    if (!card.backgroundImage) return {};
    
    // Custom uploaded image
    return {
      backgroundImage: `url(${card.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    };
  };

  // Get font family class
  const getFontClass = () => {
    const fonts = {
      arial: 'font-arial',
      playfair: 'font-playfair',
      cinzel: 'font-cinzel',
      cormorant: 'font-cormorant'
    };
    return fonts[card.fontFamily] || 'font-arial';
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div 
        className={`bg-white rounded-2xl ${card.backgroundImage ? '' : 'shadow-2xl'} overflow-hidden relative ${getFontClass()}`}
        style={{
          borderWidth: '2px',
          borderStyle: 'solid',
          borderColor: '#E5E7EB',
          color: textColor
        }}
      >
        {/* Background Image Layer */}
        {card.backgroundImage && (
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              ...getBackgroundStyle(),
              opacity: 1.0,
              zIndex: 0
            }}
          />
        )}
        {/* Inactive Card Badge */}
        {isInactive && (
          <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-1 text-xs font-semibold z-10">
            QR Code Deactivated - Card Data Preserved
          </div>
        )}
        
        <div className={`relative p-3 sm:p-6 md:p-8 ${isInactive ? 'pt-8 sm:pt-10' : ''}`} style={{ zIndex: 1 }}>
          <div className="flex flex-row gap-3 sm:gap-6 md:gap-8">
            {/* Left: QR Code (or placeholder if inactive) */}
            {!hideQR && !isInactive && (
              <div className="flex-shrink-0">
                <div 
                  className="relative p-1 rounded-2xl shadow-xl"
                  style={{ background: qrColor }}
                >
                  <div id="qr-code-share" className="rounded-xl p-1 bg-white">
                    {(card.companyLogo || card.profileImage) ? (
                      <div className="relative">
                        <QRCode 
                          value={cardUrl}
                          size={window.innerWidth < 640 ? 80 : window.innerWidth < 768 ? 120 : 160}
                          level="H"
                          fgColor={qrColor}
                          bgColor="transparent"
                        />
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <img 
                            src={card.companyLogo || card.profileImage} 
                            alt={card.companyLogo ? 'Company Logo' : card.name}
                            className={`${card.companyLogo ? 'w-8 h-8 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg' : 'w-8 h-8 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full'} border-2 border-white shadow-md object-cover bg-white p-0.5`}
                          />
                        </div>
                      </div>
                    ) : (
                      <QRCode 
                        value={cardUrl}
                        size={window.innerWidth < 640 ? 80 : window.innerWidth < 768 ? 120 : 160}
                        level="H"
                        fgColor={qrColor}
                        bgColor="transparent"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Show placeholder when QR is inactive */}
            {!hideQR && isInactive && (
              <div className="flex-shrink-0">
                <div className="relative p-2 sm:p-4 md:p-5 rounded-2xl bg-gray-200 shadow-xl">
                  <div className="bg-white rounded-xl p-1 sm:p-2 flex items-center justify-center" 
                       style={{width: window.innerWidth < 640 ? 80 : window.innerWidth < 768 ? 120 : 160, 
                               height: window.innerWidth < 640 ? 80 : window.innerWidth < 768 ? 120 : 160}}>
                    <div className="text-center text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      <p className="text-xs">QR Inactive</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Right: Profile and Info */}
            <div className="flex-1 flex flex-col justify-center gap-2 sm:gap-3 md:gap-4 min-w-0">
              {/* Profile Image and Info */}
              <div className="flex items-start gap-2 sm:gap-3">
                {/* Profile Image */}
                {card.profileImage && (
                  <div className="flex-shrink-0">
                    <img 
                      src={card.profileImage} 
                      alt={card.name}
                      className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full border-2 border-gray-200 shadow-md object-cover"
                    />
                  </div>
                )}
                
                {/* Name, Title, Company */}
                <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1">
                  <h2 className="text-lg font-bold leading-tight truncate" style={{ color: card.textColor || undefined }}>
                    {card.name}
                  </h2>
                  {card.title && (
                    <p className="text-sm font-medium truncate" style={{ color: card.textColor || undefined, opacity: 0.9 }}>
                      {card.title}
                    </p>
                  )}
                  {card.company && (
                    <p className="text-sm truncate" style={{ color: card.textColor || undefined, opacity: 0.8 }}>
                      {card.company}
                    </p>
                  )}
                </div>
              </div>

              {/* Contact and Social Icons */}
              <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 flex-wrap">
                {/* Contact Icons */}
                {card.email && (
                  <a href={`mailto:${card.email}`} onClick={(e) => { e.stopPropagation(); trackLinkClick('email', card.email); }} className="text-amber-600 hover:text-amber-800 transition-colors p-1 hover:bg-amber-50 rounded-lg" title={card.email}>
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {card.phone && (
                  <a href={`tel:${card.phone}`} onClick={(e) => { e.stopPropagation(); trackLinkClick('phone', card.phone); }} className="text-teal-600 hover:text-teal-800 transition-colors p-1 hover:bg-teal-50 rounded-lg" title={card.phone}>
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {websiteUrl && (
                  <a href={websiteUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('website', websiteUrl); }} className="text-blue-600 hover:text-blue-800 transition-colors p-1 hover:bg-blue-50 rounded-lg" title={websiteUrl}>
                    <Globe className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {card.location && (
                  <a href={card.location} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('location', card.location); }} className="text-red-600 hover:text-red-800 transition-colors p-1 hover:bg-red-50 rounded-lg" title="Location">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                
                {/* Separator */}
                {(card.email || card.phone || websiteUrl || card.location) && (card.linkedin || card.twitter || card.github || card.instagram || card.youtube || card.facebook) && (
                  <div className="w-px h-5 sm:h-5 md:h-5 bg-gray-300"></div>
                )}
                
                {/* Social Media Icons */}
                {card.linkedin && (
                  <a href={constructUrl('linkedin', card.linkedin)} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('linkedin', card.linkedin); }} className="text-blue-700 hover:text-blue-900 transition-colors p-1 hover:bg-blue-50 rounded-lg" title="LinkedIn">
                    <Linkedin className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {card.twitter && (
                  <a href={constructUrl('twitter', card.twitter)} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('twitter', card.twitter); }} className="text-sky-500 hover:text-sky-700 transition-colors p-1 hover:bg-sky-50 rounded-lg" title="Twitter">
                    <Twitter className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {card.github && (
                  <a href={constructUrl('github', card.github)} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('github', card.github); }} className="text-gray-800 hover:text-black transition-colors p-1 hover:bg-gray-50 rounded-lg" title="GitHub">
                    <Github className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {card.instagram && (
                  <a href={constructUrl('instagram', card.instagram)} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('instagram', card.instagram); }} className="text-pink-600 hover:text-pink-800 transition-colors p-1 hover:bg-pink-50 rounded-lg" title="Instagram">
                    <Instagram className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {card.youtube && (
                  <a href={constructUrl('youtube', card.youtube)} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('youtube', card.youtube); }} className="text-red-600 hover:text-red-800 transition-colors p-1 hover:bg-red-50 rounded-lg" title="YouTube">
                    <Youtube className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {card.facebook && (
                  <a href={constructUrl('facebook', card.facebook)} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('facebook', card.facebook); }} className="text-blue-600 hover:text-blue-800 transition-colors p-1 hover:bg-blue-50 rounded-lg" title="Facebook">
                    <Facebook className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
                {card.whatsapp && (
                  <a href={constructUrl('whatsapp', card.whatsapp)} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); trackLinkClick('whatsapp', card.whatsapp); }} className="text-green-600 hover:text-green-800 transition-colors p-1 hover:bg-green-50 rounded-lg" title="WhatsApp">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                  </a>
                )}
              </div>

              {/* Piper Card Branding */}
              <div className="pt-2 sm:pt-3 mt-1 sm:mt-2 border-t border-gray-200">
                <p className="text-xs sm:text-sm text-gray-500">
                  <a 
                    href="/" 
                    className="font-semibold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    Piper Card
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitingCardDisplay;
